/**
 * Hauptelement der App. Erbt vom abstrakten `PdSpaHelper` und implementiert die
 * Pflicht-Hooks (_renderRoutePages, _getFooter, _getAppLogo, _setLocale,
 * _getAppConfiguration).
 *
 * **Init-Order ist kritisch** — die statische Init unten muss vor dem
 * `@customElement`-Decorator laufen, damit Store/Router stehen wenn die ersten
 * connectedCallbacks feuern.
 *
 * Mock → Firebase Switch: passiert NICHT hier, sondern zentral in
 * `config/provider-setup.ts` (Kommentar-Toggle). Diese Datei bleibt beim
 * Wechsel unberührt.
 */

import { css, html, type PropertyValues, type TemplateResult } from "lit";
import { customElement } from "lit/decorators.js";
import { localized, msg } from "@lit/localize";

import {
  PdSpaHelper,
  startInit,
  services,
  type Footer,
} from "@progressive-development/pd-spa-helper";
import { ButtonData } from "@progressive-development/pd-forms";

import "@progressive-development/pd-shared-styles/shared-root-styles.css";
import "./common/styles/portal-fonts.css";
import "./common/styles/portal-tokens.css";
import "./common/styles/custom-overrides.css";

import {
  navigationConfig,
  storeConfig,
  ROUTE_HOME,
  ROUTE_ABOUT,
  ROUTE_APP,
  ROUTE_IMPRINT,
  ROUTE_PRIVACY,
  // Öffentliche Fach-Routen (Abschnitt 2).
  ROUTE_SITZUNGEN,
  ROUTE_SITZUNG_DETAIL,
  ROUTE_ENTSCHEIDUNGEN,
  ROUTE_WAHLEN,
  ROUTE_WAHL_DETAIL,
  ROUTE_DRUCKSACHE_DETAIL,
  ROUTE_GREMIEN,
  ROUTE_BEZIRKSVERTRETUNGEN,
  ROUTE_AUSSCHUESSE,
  ROUTE_RAT,
  ROUTE_PARTEIEN,
  ROUTE_PARTEI_DETAIL,
  ROUTE_DEZERNATE,
  ROUTE_GREMIUM_DETAIL,
  ROUTE_PERSON_DETAIL,
  ROUTE_BEZIRKE,
  ROUTE_BEZIRK,
  ROUTE_BEZIRKSVERTRETUNG,
  ROUTE_VEEDEL,
  ROUTE_QUELLEN,
  ROUTE_SITEMAP,
  hauptMenuItems,
  // Members (Abschnitt 3/4).
  ROUTE_MEMBERS_DASHBOARD,
  ROUTE_MEMBERS_PROFILE,
  ROUTE_MEMBERS_MERKLISTE,
  ROUTE_MEMBERS_MERKLISTE_DETAIL,
  ROUTE_MEMBERS_SYNC,
  // TEMPLATE-DOC: Routen der In-App-Doku.
  ROUTE_DOC_AUFBAU,
  ROUTE_DOC_PROVIDER,
  ROUTE_DOC_PATTERNS,
  ROUTE_DOC_ENTFERNEN,
} from "./config/navigation.js";
import { auswahlAusRoute } from "./modules/wahlen/wahl-auswahl.js";

import { APP_NAME, COMPANY_CONTACT, EXTERNAL_LINKS, SUPPORT_EMAIL } from "./config/app-config.js";
import { createAppProvider, PROVIDER_NAME } from "./config/provider-setup.js";
import { footerStyles } from "./common/styles/footer-styles.js";
import { besuchMelden } from "./common/zaehler/besuch-melden.js";
import {
  dokumentTitel,
  routenBeschreibung,
  routenName,
  schreibeBeschreibung,
  schreibeTitel,
} from "./common/seitentitel/seitentitel.js";
import "./common/components/app-pd-logo/app-pd-logo.js";
import { initLocale, setLocale, LANGUAGE_SELECTION_KEY } from "./localization.js";
import { logger } from "./common/logger/app-logger.js";
import { version } from "./version.js";

// Eager-Import: Default-Home für initialen Render. Alle anderen Pages werden
// in `_renderRoutePages` lazy via dynamic import geladen.
import "./modules/home/pages/default-home/default-home.js";

/*
 * Zwei Marken, zwei Rollen — sie dürfen nicht verwechselt werden:
 *
 *   portalLogoRef  die Bildmarke des ANGEBOTS (neun Felder = neun Stadtbezirke,
 *                  Fläche je Feld = Einwohneranteil; das § im Feld von Bezirk 1
 *                  ist die Portal-Kennung). Steht im Menü.
 *   app-pd-logo    die Bildmarke des ENTWICKLERS (Progressive Development),
 *                  animierter Pfadaufbau. Steht NUR in der Fußzeile — auf dem
 *                  Startbildschirm eines Bürgers wäre sie falsch. Die frühere
 *                  Wortmarke als SVG-Datei ist damit abgelöst.
 *
 * Herkunft und Zuschnitt der Bildmarke: docs/changedoc/entwurf-marke/.
 */
import portalLogoRef from "/marke/logo-hell.svg";

// =============================================================================
// Static Initialization — MUSS vor dem @customElement laufen
// =============================================================================

services.initialize(createAppProvider());

// Gespeicherte Sprachwahl übernehmen (Default: de) — vor startInit, damit
// Menü/Router direkt mit der richtigen Locale aufbauen.
const initialLocale = initLocale();

startInit({
  navigationConfigParam: navigationConfig,
  storeConfigParam: storeConfig,
  /*
   * Update-Flow (pd-spa-helper >= 1.4.0). Die Shell rendert <pd-update-prompt>
   * auf jeder Route selbst — hier steht nur, was App-Sache ist: die Texte.
   *
   * GEHÖRT ZU `registerType: "prompt"` in vite.config.ts. Ohne den wartet nie
   * ein Worker und der Dialog bleibt stumm; ohne diesen Aufruf liefe der
   * Service mit Library-Defaults. Beides zusammen ausliefern.
   *
   * Verhalten (Policy des Service, hier bewusst NICHT abgewandelt):
   *   beim Start wartender Worker  → still angewendet, ein Reload vor jeder
   *                                  Interaktion (autoApplyOnStart, Default)
   *   Fund während der Session     → dieser Dialog, nie automatisch
   *   Rückkehr aus dem Hintergrund → nur Prüfung, kein Anwenden
   *
   * Die Texte weichen von den Library-Defaults an EINER Stelle ab: Der
   * Default warnt vor verlorenen Eingaben. Das ist ein Leseportal — hier gibt
   * es keine Formulare, wohl aber eine Stelle im Dokument, an der jemand ist.
   */
  pwaUpdateConfigParam: {
    texts: {
      title: "Neue Version verfügbar",
      hint: "Aktualisieren lädt die Seite neu.",
      detail: "Mit „Später“ kommt die neue Version beim nächsten Start.",
      confirm: "Aktualisieren",
      later: "Später",
    },
  },
});

logger.info(`${APP_NAME} v${version} — provider: ${PROVIDER_NAME}`);

// Nur de — bei einer einzigen Locale blendet pd-menu den Umschalter aus.
const locales: ButtonData[] = [{ text: "🇩🇪 Deutsch", shortText: "🇩🇪", value: "de" }];

// =============================================================================
// App-Element
// =============================================================================

@customElement("politik-portal-app")
@localized()
export class PolitikPortalApp extends PdSpaHelper {
  _selectedLocale = initialLocale;

  _availableLocales = locales;

  override _singleLocaleMenu = true;

  /**
   * Reichweite der Testphase — einmal je App-Start, nach dem ersten Rendern.
   *
   * Hier und nicht in einer Seite: Gezaehlt werden BESUCHER, nicht
   * Seitenaufrufe. Ob aus dem Aufruf eine Zeile wird, entscheidet allein die
   * Function; ein Fehlschlag bleibt folgenlos (s. `besuch-melden.ts`).
   */
  override firstUpdated(geaendert: PropertyValues): void {
    super.firstUpdated(geaendert);
    besuchMelden();
  }

  /**
   * Titel und Beschreibung des Dokuments je Adresse — die erste Stufe
   * (`common/seitentitel/seitentitel.ts`): sofort beim Wechsel der Adresse,
   * mit dem Begriff der Route. Der Seitenkopf verfeinert danach aus seiner
   * Brotkrume, sobald die Seite ihre Daten hat.
   *
   * Auf `_path`, nicht nur `_route`: Von einer Drucksache zur nächsten
   * bleibt die Route gleich, die Adresse nicht.
   */
  protected override willUpdate(geaendert: PropertyValues): void {
    super.willUpdate(geaendert);
    if ((geaendert.has("_path") || geaendert.has("_route")) && this._route) {
      schreibeTitel(dokumentTitel([routenName(this._route)]));
      schreibeBeschreibung(routenBeschreibung(this._route));
    }
  }

  static override styles = [
    PdSpaHelper.styles,
    footerStyles,
    css`
      :host {
        /*
         * Menü / Logo — die drei Werte hängen zusammen, einzeln geändert
         * schneiden sie das Logo ab.
         *
         * Das Quer-Logo hat 4,69:1 (viewBox 469x100). pd-site-header setzt am
         * Bild height:100% und width:auto, die BREITE ergibt sich also aus
         * der Höhe: 52 % der Menühöhe macht 137-176 px. Die
         * max-width muss darüber liegen, sonst läuft das Bild über seinen
         * Kasten — es wird NICHT kleiner gerechnet und auch nicht
         * beschnitten (der Wrapper steht auf overflow:visible), sondern
         * schiebt sich in die Polsterung und an den Menütext heran. Genau so
         * ist es mit dem alten Wert 8rem = 128 px passiert.
         *
         * Die rechte Polsterung ist der eigentliche Abstand zum Menütext.
         * Links keine: Der Menü-Container bringt schon 1rem mit.
         */
        /*
         * Menü-Typografie: die Display-Schrift des Portals, aber kleiner und
         * leichter als die Framework-Vorgabe (1.2em bold) — fünf Punkte plus
         * Wortmarke brauchen Luft, und das Menü ist Wegweiser, nicht
         * Überschrift. Größe = Fließtext klein (15 px), Gewicht halbfett.
         */
        --pd-menu-font-family: var(--portal-font-display);
        --pd-menu-font-size: var(--portal-size-sm);
        --pd-menu-font-weight: 600;
        /*
         * DIE SEITE, AUF DER MAN IST — allein über die Helligkeit: die anderen
         * Einträge zurückgenommen, der aktuelle in vollem Papier. Ein Strich
         * darunter war zu laut; im Kopf einer Seite genügt der Unterschied,
         * ohne dass eine zweite Form hinzukommt.
         *
         * ROHWERT statt rgb(from var(--portal-papier) …): Die relative
         * Farbsyntax kann Safari erst ab 18 — davor wird die Deklaration
         * ungültig, und weil die Variable DEFINIERT ist, greift auch der
         * var()-Fallback des Frameworks nicht: Das Menü erbte dann die dunkle
         * Tinte auf Petrol. Der Wert ist --portal-papier (#edefee,
         * custom-overrides.css) mit 72 % — wer die Palette ändert, zieht ihn
         * hier nach.
         */
        --pd-menu-font-col: rgba(237, 239, 238, 0.72);
        --pd-menu-item-font-active-col: var(--portal-papier);
        --pd-menu-logo-height: 52%;
        --pd-menu-logo-maxwidth: 11.5rem;
        --pd-menu-logo-padding: 0 2.5rem 0 0;

        /*
         * Höhere Leiste als die Framework-Vorgabe (50/56/60): Mit einer
         * Wortmarke im Logo wirkte die dünne Leiste gedrängt. Die Stufen
         * folgen den Breakpoints von pd-site-header, damit die Höhe nicht an
         * einer Stelle springt, wo das Framework etwas anderes erwartet.
         *
         * ACHTUNG, hängt an --pd-menu-logo-height: 52 % von 56/64/72 px sind
         * 29-37 px Logohöhe, mal 4,69 macht 137-176 px Breite. Die max-width
         * oben muss darüber bleiben.
         */
        --pd-site-header-menu-height: 56px;

        background-color: transparent;
      }
      @media (min-width: 768px) {
        :host {
          --pd-site-header-menu-height: 64px;
        }
      }
      @media (min-width: 1024px) {
        :host {
          --pd-site-header-menu-height: 72px;
        }
      }
      main {
        max-width: var(--pd-content-max-width);
        margin: 0 auto;
        width: 100%;
        /* Seiten ohne Kopf (hideMenu) bekommen denselben Abstand. */
        padding-top: var(--portal-seitenabstand);
      }
      /*
       * Der Abstand zwischen Menü und Inhalt — für ALLE Seiten an einer Stelle.
       *
       * PdSpaHelper setzt hier padding-top: var(--pd-site-header-height); das
       * ist der VERSATZ, damit nichts unter dem fixierten Kopf verschwindet,
       * und die Höhe wird dynamisch nachgeführt. Die Luft darunter gehört der
       * App (so sieht es auch die Framework-Doku, Abschnitt 12.5). Sie hier zu
       * setzen statt je Seite ist der Unterschied zwischen einer Regel und
       * fünf: Alte Seiten (PageHostStyles), neue Portal-Seiten und künftige
       * bekommen denselben Wert.
       *
       * Die Selektor-Kombination muss header + main sein — mit einfachem main
       * gewönne die Framework-Regel.
       */
      header + main {
        padding-top: calc(var(--pd-site-header-height, 90px) + var(--portal-seitenabstand));
      }
    `,
  ];

  protected _renderRoutePages(route: string): TemplateResult {
    switch (route) {
      case ROUTE_HOME:
        return html`<app-default-home></app-default-home>`;

      case ROUTE_ABOUT:
        import("./modules/home/pages/about-page/about-page.js");
        return html`<app-about-page></app-about-page>`;

      case ROUTE_APP:
        import("./modules/home/pages/app-page/app-page.js");
        return html`<app-app-page></app-app-page>`;

      case ROUTE_IMPRINT:
        import("./modules/home/pages/imprint-page/imprint-page.js");
        return html`<app-imprint-page></app-imprint-page>`;

      case ROUTE_PRIVACY:
        import("./modules/home/pages/privacy-page/privacy-page.js");
        return html`<app-privacy-page></app-privacy-page>`;

      case ROUTE_MEMBERS_DASHBOARD:
        import("./modules/members/dashboard/pages/dashboard-page.js");
        return html`<app-dashboard-page></app-dashboard-page>`;

      case ROUTE_MEMBERS_PROFILE:
        import("./modules/members/profile/pages/profile-page.js");
        return html`<app-profile-page></app-profile-page>`;

      // Öffentliche Fach-Routen (Abschnitt 2) — echte Listen-/Detailrouten.
      case ROUTE_SITZUNGEN:
        // Sicht, Zeitraum und Filter leben im URL — deshalb `query` hinein.
        import("./modules/sitzungen/pages/sitzungen-page.js");
        return html`<app-sitzungen-page .query=${this._query ?? {}}></app-sitzungen-page>`;

      case ROUTE_SITZUNG_DETAIL:
        // Routen-Parameter aus dem Pattern "/sitzungen/:id" (der NAME lautet
        // "sitzung/detail", das MUSTER nicht) — via _params
        // (PdSpaHelper) als Property an die Page durchgereicht.
        import("./modules/sitzungen/pages/sitzung-detail-page.js");
        return html`<app-sitzung-detail-page
          sitzungId="${this._params?.id ?? ""}"
        ></app-sitzung-detail-page>`;

      case ROUTE_ENTSCHEIDUNGEN:
        // Der Filterzustand der Liste lebt im URL — deshalb `query` hinein.
        import("./modules/entscheidungen/pages/entscheidungen-page.js");
        return html`<app-entscheidungen-page
          .query=${this._query ?? {}}
        ></app-entscheidungen-page>`;

      case ROUTE_DRUCKSACHE_DETAIL:
        // Sprechender Slug aus dem Pattern "/entscheidungen/:referenz".
        import("./modules/entscheidungen/pages/drucksache-page.js");
        return html`<app-drucksache-page
          referenz="${this._params?.referenz ?? ""}"
        ></app-drucksache-page>`;

      case ROUTE_WAHLEN:
        import("./modules/wahlen/pages/wahlen-page.js");
        return html`<app-wahlen-page wahl="${this._params?.wahl ?? ""}"></app-wahlen-page>`;

      case ROUTE_WAHL_DETAIL:
        // Die drei Achsen aus der Adresse — gelesen an EINER Stelle.
        import("./modules/wahlen/pages/wahl-page.js");
        return html`<app-wahl-page .auswahl=${auswahlAusRoute(this._params)}></app-wahl-page>`;

      case ROUTE_BEZIRKSVERTRETUNGEN:
        import("./modules/bezirksvertretung/pages/bezirksvertretungen-page.js");
        return html`<app-bezirksvertretungen-page></app-bezirksvertretungen-page>`;

      case ROUTE_AUSSCHUESSE:
        import("./modules/gremien/pages/ausschuesse-page.js");
        return html`<app-ausschuesse-page></app-ausschuesse-page>`;

      case ROUTE_RAT:
        import("./modules/gremien/pages/rat-page.js");
        return html`<app-rat-page></app-rat-page>`;

      case ROUTE_PARTEIEN:
        import("./modules/parteien/pages/parteien-page.js");
        return html`<app-parteien-page></app-parteien-page>`;

      case ROUTE_PARTEI_DETAIL:
        import("./modules/parteien/pages/partei-page.js");
        return html`<app-partei-page slug="${this._params?.slug ?? ""}"></app-partei-page>`;

      case ROUTE_DEZERNATE:
        import("./modules/gremien/pages/dezernate-page.js");
        return html`<app-dezernate-page></app-dezernate-page>`;

      case ROUTE_GREMIEN:
        import("./modules/gremien/pages/gremien-page.js");
        return html`<app-gremien-page></app-gremien-page>`;

      case ROUTE_GREMIUM_DETAIL:
        import("./modules/gremien/pages/gremium-page.js");
        return html`<app-gremium-page slug="${this._params?.slug ?? ""}"></app-gremium-page>`;

      case ROUTE_PERSON_DETAIL:
        import("./modules/gremien/pages/person-detail-page.js");
        return html`<app-person-detail-page
          personId="${this._params?.id ?? ""}"
        ></app-person-detail-page>`;

      case ROUTE_BEZIRKSVERTRETUNG:
        import("./modules/bezirksvertretung/pages/bezirksvertretung-page.js");
        return html`<app-bezirksvertretung-page
          bezirk="${this._params?.bezirk ?? ""}"
        ></app-bezirksvertretung-page>`;

      case ROUTE_BEZIRKE:
        import("./modules/gebiet/pages/bezirke-page.js");
        return html`<app-bezirke-page></app-bezirke-page>`;

      case ROUTE_BEZIRK:
        // Ohne Bezirk gibt es kein Profil — dann die Übersicht der Gebiete.
        if (!this._params?.bezirk) {
          import("./modules/gebiet/pages/bezirke-page.js");
          return html`<app-bezirke-page></app-bezirke-page>`;
        }
        import("./modules/bezirk/pages/bezirk-page.js");
        return html`<app-bezirk-page bezirk="${this._params.bezirk}"></app-bezirk-page>`;

      case ROUTE_VEEDEL:
        import("./modules/veedel/pages/veedel-page.js");
        return html`<app-veedel-page
          stadtteil="${this._params?.stadtteil ?? ""}"
        ></app-veedel-page>`;

      case ROUTE_QUELLEN:
        import("./modules/rahmen/pages/quellen-page.js");
        return html`<app-quellen-page></app-quellen-page>`;

      case ROUTE_SITEMAP:
        import("./modules/rahmen/pages/sitemap-page.js");
        return html`<app-sitemap-page></app-sitemap-page>`;

      // Members: Merkliste (bookmarks) + Sync-Status (Admin).
      case ROUTE_MEMBERS_MERKLISTE:
        import("./modules/members/bookmarks/pages/bookmarks-page.js");
        return html`<app-bookmarks-page></app-bookmarks-page>`;

      case ROUTE_MEMBERS_MERKLISTE_DETAIL:
        import("./modules/members/bookmarks/pages/bookmark-detail-page.js");
        return html`<app-bookmark-detail-page
          bookmarkId="${this._params?.bookmarkId ?? ""}"
        ></app-bookmark-detail-page>`;

      case ROUTE_MEMBERS_SYNC:
        import("./modules/members/sync/pages/sync-status-page.js");
        return html`<app-sync-status-page></app-sync-status-page>`;

      // TEMPLATE-DOC: Render-Cases der In-App-Doku — Block komplett löschbar.
      case ROUTE_DOC_AUFBAU:
        import("./modules/template-doc/pages/doc-aufbau-page.js");
        return html`<app-doc-aufbau-page></app-doc-aufbau-page>`;

      case ROUTE_DOC_PROVIDER:
        import("./modules/template-doc/pages/doc-provider-page.js");
        return html`<app-doc-provider-page></app-doc-provider-page>`;

      case ROUTE_DOC_PATTERNS:
        import("./modules/template-doc/pages/doc-patterns-page.js");
        return html`<app-doc-patterns-page></app-doc-patterns-page>`;

      case ROUTE_DOC_ENTFERNEN:
        import("./modules/template-doc/pages/doc-entfernen-page.js");
        return html`<app-doc-entfernen-page></app-doc-entfernen-page>`;

      default:
        return html`<p>Seite nicht gefunden</p>`;
    }
  }

  // ---------------------------------------------------------------------------
  // Fuß (FACHKONZEPT-rahmen.md, 3) — vier Blöcke und eine Zeile: Themen
  // (Spiegel des Hauptmenüs — DIESELBE Liste, plus Parteien) · Daten und
  // Quellen (Vertrauensblock; extern als ↗ und in neuem Tab; die
  // Pflicht-Attribution ist hier zugleich der Weg auf die Datensatzseite) ·
  // Über das Portal · Betreiber (logo-Slot, s. _renderFooterSlotContent).
  // Die Zeile: Impressum · Datenschutz · Sitemap · Version · Gemacht von.
  // ---------------------------------------------------------------------------

  protected _getFooter(): Footer {
    const extern = (url: string) => () => window.open(url, "_blank", "noopener");
    return {
      sections: [
        {
          heading: msg("Themen", { id: "app.footer.themen" }),
          links: [
            ...hauptMenuItems().map((m) => ({ key: m.key, name: m.name, link: m.route })),
            {
              key: "parteien",
              name: msg("Parteien", { id: "app.footer.parteien" }),
              link: `/${ROUTE_PARTEIEN}`,
            },
          ],
        },
        {
          heading: msg("Daten und Quellen", { id: "app.footer.quellen" }),
          links: [
            {
              key: "quellen",
              name: msg("Quellen und Stand", { id: "app.quellen.titel" }),
              link: `/${ROUTE_QUELLEN}`,
            },
            {
              key: "ris",
              name: msg("Ratsinformationssystem ↗", { id: "app.footer.ris" }),
              action: extern(EXTERNAL_LINKS.ris),
            },
            {
              /*
               * NUR der Kurzname, nicht die volle Pflicht-Attribution: In der
               * schmalen Fußspalte lief der ganze Wortlaut über zwei Zeilen und
               * sprengte den Block.
               *
               * DIE AUFLAGE BLEIBT ERFÜLLT — der exakte Wortlaut
               * (DATA_SOURCE_ATTRIBUTION) steht unverändert an zwei Stellen:
               * auf /quellen im Abschnitt "Woher kommt was?" und im Impressum
               * unter "Angezeigte Daten". Hier steht der Weg zur Quelle, nicht
               * die Nennung selbst. Wer den Wortlaut ändert, ändert ihn NICHT
               * hier, sondern in config/app-config.ts — und dann nirgends.
               */
              key: "attribution",
              name: "offenedaten-koeln.de ↗",
              action: extern(EXTERNAL_LINKS.openData),
            },
          ],
        },
        {
          heading: msg("Über das Portal", { id: "app.menu.about" }),
          links: [
            {
              key: "about",
              name: msg("Über das Portal", { id: "app.menu.about" }),
              link: `/${ROUTE_ABOUT}`,
            },
            {
              key: "installieren",
              name: msg("Als App installieren", { id: "app.installieren.pfad" }),
              link: `/${ROUTE_APP}`,
            },
            {
              key: "sitemap",
              name: msg("Sitemap", { id: "app.sitemap.pfad" }),
              link: `/${ROUTE_SITEMAP}`,
            },
          ],
        },
      ],
      legalLinks: [
        {
          key: "imprint",
          name: msg("Impressum", { id: "app.menu.imprint" }),
          link: `/${ROUTE_IMPRINT}`,
        },
        {
          key: "privacy",
          name: msg("Datenschutz", { id: "app.menu.privacy" }),
          link: `/${ROUTE_PRIVACY}`,
        },
        {
          key: "sitemap-zeile",
          name: msg("Sitemap", { id: "app.sitemap.pfad" }),
          link: `/${ROUTE_SITEMAP}`,
        },
      ],
      /*
       * KEIN Copyright-Vermerk. Der Inhalt dieses Portals gehört überwiegend
       * nicht dem Betreiber, sondern der Stadt — das Impressum sagt es
       * ausdrücklich ("Angezeigte Daten"). Ein "© PD" darüber widerspräche der
       * eigenen Seite. Auch kein `madeBy`: Die Urheberschaft steht als Marke im
       * logo-Slot, einmal statt dreimal.
       */
      // AUSDRUECKLICH LEER, nicht weggelassen: PdSpaHelper faellt sonst auf
      // seine Vorgabe zurueck ("PD Progressive Development UG" bzw. MADE_BY).
      // Nur `undefined` heisst dort "nimm die Vorgabe", `""` heisst "nichts".
      copyright: "",
      madeBy: { txt: "" },
      version,
      showVersion: true,
    };
  }

  /**
   * Der logo-Slot des pd-footer — die EINZIGE Nennung des Hauses.
   *
   * Vorher stand der Name dreimal im Fuß: hier, im Copyright und im
   * "Gemacht von". Drei Nennungen für zwei Fragen, von denen eine falsch war.
   *
   * Die Marke groß, der Name klein: Ein Zeichen liest sich als Handschrift,
   * ein Name mit Anschrift als Verantwortung. Die Gewichtung ist Absicht —
   * die Entwicklung ist das, wofür dieses Haus stehen will, der Betrieb ist
   * Pflicht und soll später ohnehin an einen Träger übergehen. Wechselt er,
   * bleibt die Marke und die Zeile darunter wird getauscht.
   */
  protected override _renderFooterSlotContent(): TemplateResult {
    return html`
      <div slot="logo" class="footer-brand-block">
        <!--
          Die Beschriftung steht ÜBER dem Schriftzug: Er trägt den Namen selbst,
          also braucht es keine zweite Zeile mit demselben Namen. Beide Rollen
          werden genannt, solange dasselbe Haus beides ist — wechselt der
          Betreiber, wird daraus "Entwickelt von" und eine eigene Betreiberzeile.
        -->
        <p class="kennung">
          ${msg("Entwickelt und betrieben von", { id: "app.footer.entwickelt" })}
        </p>
        <a
          class="marke"
          href="https://progressive-development.com"
          target="_blank"
          rel="noopener"
          aria-label=${COMPANY_CONTACT.companyName}
        >
          <app-pd-logo></app-pd-logo>
        </a>
        <!--
          NUR die Mail: Der Ort stand als einzelnes Wort unter dem Schriftzug
          verloren da und beantwortete keine Frage — die vollständige Anschrift
          steht ohnehin im Impressum, und dorthin führt die untere Zeile.
        -->
        <address>
          <a href="mailto:${SUPPORT_EMAIL}">${SUPPORT_EMAIL}</a>
        </address>
      </div>
    `;
  }

  protected _getAppLogo(): TemplateResult {
    // Die helle Fassung, weil der Menügrund Petrol ist (--pd-menu-bg-col).
    return html`<img class="logo" slot="logo" src="${portalLogoRef}" alt="${APP_NAME}" />`;
  }

  protected _setLocale(locale: string): void {
    setLocale(locale);
    if (this._availableLocales.some((l) => l.value === locale)) {
      localStorage.setItem(LANGUAGE_SELECTION_KEY, locale);
    }
  }

  protected _getAppConfiguration(): unknown {
    return {
      appName: APP_NAME,
      version,
      companyContact: COMPANY_CONTACT,
      supportEmail: SUPPORT_EMAIL,
    };
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "politik-portal-app": PolitikPortalApp;
  }
}
